// Drill-through tab: one card per DRILL_THROUGH entry, any number open at once.
// A tile may have SEVERAL links, one per column (SOURCE_COLUMN): a click on a
// column follows that column's link, otherwise the tile's "Any column" link;
// two links on one column → people choose from a short menu at run time
// (@twasta/visual-engine resolveDrillThrough + DrillChooser).

import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Link2, Plus, Trash2, ArrowRight, Info } from 'lucide-react'
import {
  loadDashboardObject, loadVisualObject,
  type ProjectDashboardSummary, type ProjectVisualSummary,
} from '@/api/designers'
import type { DashboardBody, DashboardDrillTargetType, DashboardDrillThrough } from './types'
import { sanitizeDashboardModel } from './helpers'
import { extractVisualParams } from './visualParams'
import { visualColumns, visualRowColumns } from './visualColumns'
import { Chevron, tileContentKey } from './designerShared'
import ObjectPickerField from '../page/ObjectPickerField'
import { DRILL_CLICKED_SOURCES, isClickedColumnSource } from '@twasta/visual-engine'
import { humanizeObjName } from './AddVisualModal'

const TARGETS: Array<{ value: DashboardDrillTargetType; title: string; detail: string }> = [
  { value: 'dashboard', title: 'Dashboard', detail: 'Opens as a new level with a breadcrumb and Back.' },
  { value: 'visual', title: 'Visual', detail: 'Opens one chart or table as a new level.' },
  { value: 'self', title: 'This dashboard (cross-filter)', detail: "Applies the mapped values to this dashboard's global criteria." },
]

export interface DrillPanelProps {
  canEdit: boolean
  project: string
  body: DashboardBody
  projectVisuals: ProjectVisualSummary[]
  projectDashboards: ProjectDashboardSummary[]
  visualContents: Record<string, unknown | null>
  open: ReadonlySet<number>
  focused: number | null
  onToggle: (idx: number) => void
  onToggleAll: () => void
  onAdd: () => void
  onDelete: (idx: number) => void
  onFieldChange: <K extends keyof DashboardDrillThrough>(idx: number, field: K, value: DashboardDrillThrough[K]) => void
  onMappingChange: (idx: number, oldKey: string, newKey: string, newValue: string) => void
  /** `source` = add that exact mapping source (a clicked-column one). */
  onAddMapping: (idx: number, source?: string) => void
  onDeleteMapping: (idx: number, key: string) => void
}

export default function DrillPanel(p: DrillPanelProps) {
  const list = p.body.DRILL_THROUGH
  const allOpen = list.length > 0 && list.every((_, i) => p.open.has(i))
  // Target params offered in the mapping, cached per target (`D:<obj>` = that
  // dashboard's GLOBAL_CRITERIA, `V:<obj>` = that visual's prompt params).
  // null = the fetch failed → the mapping falls back to free text.
  const [targetParams, setTargetParams] = useState<Record<string, string[] | null>>({})
  const wantedKeys = useMemo(() => {
    const keys = new Set<string>()
    list.forEach((d, i) => {
      if (!p.open.has(i)) return
      if (d.TARGET_TYPE === 'dashboard' && d.TARGET_DASHBOARD_ID) keys.add(`D:${d.TARGET_DASHBOARD_ID}`)
      if (d.TARGET_TYPE === 'visual' && d.TARGET_VISUAL_ID) keys.add(`V:${d.TARGET_VISUAL_ID}`)
    })
    return [...keys]
  }, [list, p.open])
  useEffect(() => {
    const missing = wantedKeys.filter((k) => !(k in targetParams))
    if (!p.project || missing.length === 0) return
    let cancelled = false
    for (const key of missing) {
      const kind = key.slice(0, 1)
      const obj = key.slice(2)
      const load = async (): Promise<string[]> => {
        if (kind === 'D') {
          const res = await loadDashboardObject(p.project, obj)
          if (!res?.content) throw new Error(`Dashboard ${obj} has no model`)
          return sanitizeDashboardModel(res.content, obj).DASHBOARD.GLOBAL_CRITERIA.map((c) => c.PARAM_NAME).filter(Boolean)
        }
        const res = await loadVisualObject(p.project, obj)
        if (!res?.content) throw new Error(`Visual ${obj} has no model`)
        return extractVisualParams(res.content).map((x) => x.name)
      }
      load()
        .then((names) => { if (!cancelled) setTargetParams((prev) => ({ ...prev, [key]: names })) })
        .catch((err) => {
          console.error('Failed to load drill-through target params', key, err)
          if (cancelled) return
          setTargetParams((prev) => ({ ...prev, [key]: null }))
          toast.error(`Could not read the parameters of ${obj}. Type the target parameter instead.`)
        })
    }
    return () => { cancelled = true }
  }, [p.project, wantedKeys, targetParams])

  return (
    <>
      <div className="dd-phead">
        <div className="t">
          <div className="dd-ptitle">Drill-through</div>
          <p className="dd-hint" style={{ marginTop: 4 }}>Clicking a row or point on a tile opens more detail.</p>
        </div>
        {list.length > 0 && (
          <button className="dd-tb link" style={{ height: 34 }} onClick={p.onToggleAll}>
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
        {p.canEdit && (
          <button className="dd-tb pri" style={{ height: 34 }} onClick={p.onAdd}><Plus size={15} /> Add</button>
        )}
      </div>
      <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {list.map((d, i) => (
          <LinkCard
            key={i}
            {...p}
            d={d}
            idx={i}
            isOpen={p.open.has(i)}
            isFocused={p.focused === i}
            targetParams={
              d.TARGET_TYPE === 'self'
                ? p.body.GLOBAL_CRITERIA.map((c) => c.PARAM_NAME).filter(Boolean)
                : d.TARGET_TYPE === 'visual' && d.TARGET_VISUAL_ID
                  ? targetParams[`V:${d.TARGET_VISUAL_ID}`] ?? null
                  : d.TARGET_TYPE === 'dashboard' && d.TARGET_DASHBOARD_ID
                    ? targetParams[`D:${d.TARGET_DASHBOARD_ID}`] ?? null
                    : null
            }
          />
        ))}
        {list.length === 0 && (
          <p className="dd-hint">No drill-through links. Add one to open more detail from a tile.</p>
        )}
        <div className="dd-info">
          <Info size={16} style={{ flexShrink: 0, marginTop: 2, color: 'var(--color-text-faint)' }} />
          <span>
            A tile can have several drill-throughs, one per column. A click follows the one for the column
            clicked, otherwise the tile&apos;s “Any column” one. Double-click always shows the underlying data.
          </span>
        </div>
      </div>
    </>
  )
}

function LinkCard(p: DrillPanelProps & {
  d: DashboardDrillThrough; idx: number; isOpen: boolean; isFocused: boolean; targetParams: string[] | null
}) {
  const { d, idx, body, canEdit } = p
  const src = body.VISUALS.find((t) => t.VISUAL_ID === d.SOURCE_VISUAL_ID) || null
  const content = src ? p.visualContents[tileContentKey(src)] : undefined
  const cols = useMemo(() => (src && src.TILE_KIND !== 'report' && content ? visualColumns(content) : []), [src, content])
  const rowCols = useMemo(() => (content ? visualRowColumns(content) : []), [content])
  const col = (d.SOURCE_COLUMN || '').trim()
  const colLabel = col ? (cols.find((c) => c.name.toLowerCase() === col.toLowerCase())?.label || col) : 'Any column'
  const siblings = body.DRILL_THROUGH.filter((x) => x.SOURCE_VISUAL_ID === d.SOURCE_VISUAL_ID && d.SOURCE_VISUAL_ID)
  const dup = siblings.filter((x) => (x.SOURCE_COLUMN || '').trim().toLowerCase() === col.toLowerCase()).length > 1
  const target = d.TARGET_TYPE === 'self'
    ? 'Filter this dashboard'
    : d.TARGET_TYPE === 'visual'
      ? (d.TARGET_VISUAL_ID ? humanizeObjName(d.TARGET_VISUAL_ID) : 'No visual picked')
      : (d.TARGET_DASHBOARD_ID
        ? (p.projectDashboards.find((x) => x.obj_name === d.TARGET_DASHBOARD_ID)?.dashboard_name || humanizeObjName(d.TARGET_DASHBOARD_ID))
        : 'No dashboard picked')
  const nMap = Object.keys(d.PARAM_MAPPING).length
  // Clicked-column sources not used yet on this link (each may appear once).
  const freeClicked = DRILL_CLICKED_SOURCES.filter((c) => !Object.keys(d.PARAM_MAPPING).some((k) => k.trim().toLowerCase() === c.key))
  const usesClicked = Object.keys(d.PARAM_MAPPING).some(isClickedColumnSource)
  const head = `${src ? (src.VISUAL_NAME || src.VISUAL_ID) : 'No source tile'} · ${colLabel}`
  const summary = `→ ${target}${nMap ? ` · ${nMap} value${nMap === 1 ? '' : 's'} passed` : ''}`
  const id = `dd-l${idx}`
  const listId = `${id}-cols`

  return (
    <div className={`dd-fcard${p.isOpen ? ' open' : ''}${p.isFocused ? ' on' : ''}`}>
      <button className="dd-fcard-h" onClick={() => p.onToggle(idx)} aria-expanded={p.isOpen}>
        <span className="dd-glyph link"><Link2 size={17} /></span>
        <span className="t"><b>{head}</b><span>{summary}</span></span>
        <Chevron open={p.isOpen} />
      </button>
      {p.isOpen && (
        <fieldset disabled={!canEdit} className="dd-fbody"
          style={{ margin: 0, minWidth: 0, border: 'none', borderTop: '1px solid var(--color-border)' }}>
          <div style={{ marginTop: 10 }}>
            <label className="dd-lab" htmlFor={`${id}-src`}>Source visual</label>
            <select id={`${id}-src`} className="dd-inp" value={d.SOURCE_VISUAL_ID}
              onChange={(e) => {
                p.onFieldChange(idx, 'SOURCE_VISUAL_ID', e.target.value)
                if (e.target.value !== d.SOURCE_VISUAL_ID) p.onFieldChange(idx, 'SOURCE_COLUMN', undefined)
              }}>
              <option value="">— pick a tile —</option>
              {d.SOURCE_VISUAL_ID && !src && <option value={d.SOURCE_VISUAL_ID}>{d.SOURCE_VISUAL_ID} (not on this dashboard)</option>}
              {body.VISUALS.map((t) => <option key={t.VISUAL_ID} value={t.VISUAL_ID}>{t.VISUAL_NAME || t.VISUAL_ID} · {t.VISUAL_ID}</option>)}
            </select>
            <p className="dd-hint">Or click a tile on the grid to make it the source.</p>
          </div>

          <div>
            <div className="dd-lab">When someone clicks</div>
            <div className="dd-pills" role="radiogroup" aria-label="Column that opens this link">
              <button type="button" className="dd-pill" aria-pressed={!col} onClick={() => p.onFieldChange(idx, 'SOURCE_COLUMN', undefined)}>
                Any column
              </button>
              {cols.map((c) => (
                <button key={c.name} type="button" className="dd-pill"
                  aria-pressed={col.toLowerCase() === c.name.toLowerCase()}
                  title={`${c.label} — ${c.group}`}
                  onClick={() => p.onFieldChange(idx, 'SOURCE_COLUMN', c.name)}>
                  {c.label} <span className="pn">{c.name}</span>
                </button>
              ))}
              {col && !cols.some((c) => c.name.toLowerCase() === col.toLowerCase()) && (
                <span className="dd-pill miss" title="The source visual does not show this column">
                  {col} <span className="pn">(not in the visual)</span>
                </span>
              )}
            </div>
            {src?.TILE_KIND === 'report' && (
              <p className="dd-hint">A report tile cannot tell which column was clicked, so only “Any column” applies.</p>
            )}
            {dup && (
              <p className="dd-hint dd-warn">
                Another drill-through on this tile uses the same column. At run time people choose which one to open from a short menu.
              </p>
            )}
            {siblings.length > 1 && (
              <p className="dd-hint">This tile has {siblings.length} drill-throughs, one per column you pick.</p>
            )}
            <p className="dd-hint">
              Pick a column to give it its own target. “Any column” is used wherever no column has one of its own.
            </p>
          </div>

          <div>
            <div className="dd-lab">Target type</div>
            <div role="radiogroup" aria-label="Target type" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {TARGETS.map((t) => (
                <button key={t.value} type="button" role="radio" className="dd-opt"
                  aria-checked={d.TARGET_TYPE === t.value}
                  onClick={() => p.onFieldChange(idx, 'TARGET_TYPE', t.value)}>
                  <span className="dd-rad" />
                  <span><b>{t.title}</b><span className="d">{t.detail}</span></span>
                </button>
              ))}
            </div>
          </div>

          {d.TARGET_TYPE === 'dashboard' && (
            <div>
              <div className="dd-lab">Target dashboard</div>
              <ObjectPickerField
                value={d.TARGET_DASHBOARD_ID}
                onChange={(obj) => p.onFieldChange(idx, 'TARGET_DASHBOARD_ID', obj)}
                catalog={p.projectDashboards.map((x) => ({
                  obj_name: x.obj_name,
                  title: x.dashboard_name || x.obj_name,
                  hint: `${x.visual_count} tile${x.visual_count === 1 ? '' : 's'}`,
                }))}
                typeLabel="dashboard"
                placeholder="Pick a dashboard…"
                emptyHint="No dashboards found in this project."
              />
            </div>
          )}
          {d.TARGET_TYPE === 'visual' && (
            <div>
              <div className="dd-lab">Target visual</div>
              <ObjectPickerField
                value={d.TARGET_VISUAL_ID || ''}
                onChange={(obj) => p.onFieldChange(idx, 'TARGET_VISUAL_ID', obj)}
                catalog={p.projectVisuals.map((v) => ({
                  obj_name: v.obj_name,
                  title: humanizeObjName(v.obj_name),
                  hint: v.visual_type || v.schema_name || undefined,
                }))}
                typeLabel="visual"
                placeholder="Pick a visual…"
                emptyHint="No visuals found in this project."
              />
            </div>
          )}

          <div>
            <div className="dd-sec-h" style={{ margin: '2px 0 8px' }}>
              Values passed
              {canEdit && (
                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
                  {freeClicked.length > 0 && (
                    <button type="button" className="dd-tb link" style={{ height: 26, textTransform: 'none', letterSpacing: 0 }}
                      title="Send which column was clicked — its name or its heading"
                      onClick={() => p.onAddMapping(idx, freeClicked[0].key)}>
                      + Clicked column
                    </button>
                  )}
                  <button type="button" className="dd-tb link" style={{ height: 26, textTransform: 'none', letterSpacing: 0 }}
                    onClick={() => p.onAddMapping(idx)}>
                    + Add
                  </button>
                </span>
              )}
            </div>
            {nMap === 0 ? (
              <p className="dd-hint" style={{ margin: 0 }}>
                {d.TARGET_TYPE === 'self'
                  ? "None yet. Add one to set this dashboard's criteria from the clicked row."
                  : 'None yet. Add one to fill the target’s filter from the clicked row.'}
              </p>
            ) : (
              <>
                <div className="dd-map" style={{ fontSize: 13, color: 'var(--color-text-faint)', marginBottom: 4 }}>
                  <span>From the click</span><span /><span>{d.TARGET_TYPE === 'self' ? 'This dashboard’s criterion' : 'Target parameter'}</span><span />
                </div>
                <datalist id={listId}>
                  {rowCols.map((c) => <option key={c} value={c} />)}
                </datalist>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {/* Keyed by position, not by column name: typing a name renames
                      the row on every keystroke, and a name key would remount the
                      input and drop focus. Renames keep the row's position. */}
                  {Object.entries(d.PARAM_MAPPING).map(([from, to], mi) => (
                    <div key={mi} className="dd-map">
                      {isClickedColumnSource(from) ? (
                        <select className="dd-inp" style={{ height: 38 }} value={from.trim().toLowerCase()}
                          aria-label="What to send about the clicked column"
                          title={DRILL_CLICKED_SOURCES.find((c) => c.key === from.trim().toLowerCase())?.hint}
                          onChange={(e) => p.onMappingChange(idx, from, e.target.value, to)}>
                          {DRILL_CLICKED_SOURCES.map((c) => (
                            <option key={c.key} value={c.key} title={c.hint}
                              disabled={c.key !== from.trim().toLowerCase() && c.key in d.PARAM_MAPPING}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input className="dd-inp mono" style={{ height: 38 }} value={from} list={listId}
                          aria-label="Source column" placeholder="SOURCE_COLUMN"
                          onChange={(e) => p.onMappingChange(idx, from, e.target.value, to)} />
                      )}
                      <ArrowRight size={18} style={{ color: 'var(--color-text-faint)' }} aria-hidden="true" />
                      {p.targetParams && p.targetParams.length > 0 ? (
                        <select className="dd-inp mono" style={{ height: 38 }} value={to} aria-label="Target parameter"
                          onChange={(e) => p.onMappingChange(idx, from, from, e.target.value)}>
                          <option value="">— target param —</option>
                          {to !== '' && !p.targetParams.includes(to) && <option value={to}>{to} (custom)</option>}
                          {p.targetParams.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      ) : (
                        <input className="dd-inp mono" style={{ height: 38 }} value={to} aria-label="Target parameter"
                          placeholder="TARGET_PARAM" onChange={(e) => p.onMappingChange(idx, from, from, e.target.value)} />
                      )}
                      <button type="button" className="vdo-ib sm" aria-label="Remove this value" onClick={() => p.onDeleteMapping(idx, from)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                {usesClicked && (
                  <p className="dd-hint" style={{ marginTop: 6 }}>
                    {col
                      ? `Column name / Column heading send the ${colLabel} column — the column this link opens from.`
                      : 'Column name / Column heading send the column the person clicked: a grid cell’s column, a chart point’s value column, a pivot cell’s measure.'}
                    {src?.TILE_KIND === 'report' ? ' A report tile cannot tell which column was clicked, so nothing is sent.' : ''}
                  </p>
                )}
              </>
            )}
          </div>
          {canEdit && (
            <div style={{ display: 'flex' }}>
              <button type="button" className="dd-tb danger sm" style={{ marginLeft: 'auto' }} onClick={() => p.onDelete(idx)}>
                Remove link
              </button>
            </div>
          )}
        </fieldset>
      )}
    </div>
  )
}
